import React, { useState, useEffect } from 'react';
import pb from '../../../lib/pocketbase';
import './BookingSystem.css';

const BookingSystem = ({ user }) => {
    const [step, setStep] = useState(1);
    const [loading, setLoading] = useState(false);
    const [userVehicles, setUserVehicles] = useState([]);

    const [bookingData, setBookingData] = useState({
        service: '',
        vehicle_brand: '',
        vehicle_model: '',
        vehicle_plate: '',
        date: '',
        time: '',
        firstName: user?.firstName || '',
        lastName: user?.lastName || '',
        phone: user?.phone || '',
        email: user?.email || ''
    });

    const services = [
        { id: 'cle', label: 'Reproduction de clé', icon: '🔑', duration: '30-45 min' },
        { id: 'ethanol', label: 'Reprogrammation Éthanol', icon: '⛽', duration: '1h-2h' },
        { id: 'diag', label: 'Diagnostic électronique', icon: '💻', duration: '45 min' },
    ];

    const [availableDates, setAvailableDates] = useState([]);
    const [availableSlots, setAvailableSlots] = useState([]);

    useEffect(() => {
        // Fetch known vehicles for this user from history
        const fetchVehicles = async () => {
            try {
                const [quotes, invoices] = await Promise.all([
                    pb.collection('quotes').getFullList({ filter: `user = "${user.id}" || email = "${user.email}"` }),
                    pb.collection('invoices').getFullList({ filter: `user = "${user.id}" || email = "${user.email}"` })
                ]);

                const combined = [...quotes, ...invoices]
                    .filter(item => item.vehicle_brand && item.vehicle_model)
                    .map(item => ({
                        brand: item.vehicle_brand,
                        model: item.vehicle_model,
                        plate: item.vehicle_plate || ''
                    }));

                // Unique by plate
                const unique = Array.from(new Set(combined.map(v => `${v.brand}|${v.model}|${v.plate}`)))
                    .map(s => {
                        const [brand, model, plate] = s.split('|');
                        return { brand, model, plate };
                    });

                setUserVehicles(unique);
            } catch (err) {
                console.error("Vehicles fetch error:", err);
            }
        };

        fetchVehicles();

        // Generate next 21 available days (excluding Sundays)
        const dates = [];
        let curr = new Date();
        curr.setHours(0, 0, 0, 0);

        while (dates.length < 21) {
            curr.setDate(curr.getDate() + 1);
            if (curr.getDay() !== 0) { // Skip Sunday
                dates.push(new Date(curr));
            }
        }
        setAvailableDates(dates);
    }, [user.id, user.email]);

    const handleServiceSelect = (id) => {
        setBookingData({ ...bookingData, service: id });
        setStep(2);
    };

    const handleVehicleSelect = (v) => {
        setBookingData({
            ...bookingData,
            vehicle_brand: v.brand,
            vehicle_model: v.model,
            vehicle_plate: v.plate
        });
        setStep(3);
    };

    const handleDateSelect = async (dateObj) => {
        const dateStr = dateObj.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' });
        setBookingData({ ...bookingData, date: dateStr });
        setLoading(true);
        try {
            const existing = await pb.collection('appointments').getList(1, 100, {
                filter: `date = "${dateStr}"`,
            });
            const bookedTimes = existing.items.map(apt => apt.time);
            const baseSlots = ['09:00', '10:00', '11:00', '14:00', '15:00', '16:00', '17:00'];
            setAvailableSlots(baseSlots.filter(s => !bookedTimes.includes(s)));
            setStep(4);
        } catch (err) {
            setStep(4);
        } finally {
            setLoading(false);
        }
    };

    const handleTimeSelect = (time) => {
        setBookingData({ ...bookingData, time });
        setStep(5);
    };

    const handleConfirmBooking = async () => {
        setLoading(true);
        try {
            const data = {
                users: user.id,
                service: bookingData.service,
                date: bookingData.date,
                time: bookingData.time,
                vehicle_details: `${bookingData.vehicle_brand} ${bookingData.vehicle_model} (${bookingData.vehicle_plate})`.trim(),
                firstName: bookingData.firstName,
                lastName: bookingData.lastName,
                phone: bookingData.phone,
                email: bookingData.email,
                status: 'pending'
            };
            await pb.collection('appointments').create(data);
            setStep(6);
        } catch (err) {
            alert('Erreur lors de la réservation : ' + err.message);
        } finally {
            setLoading(false);
        }
    };

    const resetBooking = () => {
        setBookingData({
            ...bookingData,
            service: '', date: '', time: '',
            vehicle_brand: '', vehicle_model: '', vehicle_plate: ''
        });
        setStep(1);
    };

    return (
        <div className="booking-container">
            <div className="booking-stepper">
                {[1, 2, 3, 4, 5].map(s => (
                    <React.Fragment key={s}>
                        <div className={`step-dot ${step >= s ? 'active' : ''}`}>{s}</div>
                        {s < 5 && <div className={`step-line ${step > s ? 'active' : ''}`}></div>}
                    </React.Fragment>
                ))}
            </div>

            {loading && <div className="loader-overlay">Chargement...</div>}

            {/* STEP 1: SERVICE */}
            {step === 1 && (
                <div className="booking-step-content animation-fadeIn">
                    <h3>Quel service souhaitez-vous ?</h3>
                    <div className="service-selection-grid">
                        {services.map(s => (
                            <button key={s.id} className="service-card-btn" onClick={() => handleServiceSelect(s.id)}>
                                <span className="service-icon-large">{s.icon}</span>
                                <span className="service-label-large">{s.label}</span>
                                <span className="service-duration">Durée estimée : {s.duration}</span>
                            </button>
                        ))}
                    </div>
                </div>
            )}

            {/* STEP 2: VEHICLE */}
            {step === 2 && (
                <div className="booking-step-content animation-fadeIn">
                    <button className="back-btn" onClick={() => setStep(1)}>← Retour</button>
                    <h3>Votre Véhicule</h3>

                    {userVehicles.length > 0 && (
                        <div style={{ marginBottom: '2rem' }}>
                            <p style={{ color: '#94a3b8', marginBottom: '1rem' }}>Historique de vos véhicules :</p>
                            <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', justifyContent: 'center' }}>
                                {userVehicles.map((v, i) => (
                                    <button
                                        key={i}
                                        className="date-select-card"
                                        style={{ height: 'auto', padding: '1rem', width: '200px' }}
                                        onClick={() => handleVehicleSelect(v)}
                                    >
                                        <span style={{ fontWeight: 'bold' }}>{v.brand} {v.model}</span>
                                        <span style={{ fontSize: '0.8rem', color: '#64748b' }}>{v.plate}</span>
                                    </button>
                                ))}
                            </div>
                            <div style={{ margin: '2rem 0', height: '1px', background: 'rgba(255,255,255,0.1)' }}>OU</div>
                        </div>
                    )}

                    <div style={{ maxWidth: '400px', margin: '0 auto', textAlign: 'left' }}>
                        <p style={{ color: '#94a3b8', marginBottom: '1rem' }}>Saisir un autre véhicule :</p>
                        <div className="form-group" style={{ marginBottom: '1rem' }}>
                            <label>Marque</label>
                            <input
                                className="admin-input"
                                value={bookingData.vehicle_brand}
                                onChange={e => setBookingData({ ...bookingData, vehicle_brand: e.target.value })}
                                placeholder="ex: Peugeot"
                            />
                        </div>
                        <div className="form-group" style={{ marginBottom: '1rem' }}>
                            <label>Modèle</label>
                            <input
                                className="admin-input"
                                value={bookingData.vehicle_model}
                                onChange={e => setBookingData({ ...bookingData, vehicle_model: e.target.value })}
                                placeholder="ex: 308"
                            />
                        </div>
                        <div className="form-group" style={{ marginBottom: '1.5rem' }}>
                            <label>Immatriculation (optionnel)</label>
                            <input
                                className="admin-input"
                                value={bookingData.vehicle_plate}
                                onChange={e => setBookingData({ ...bookingData, vehicle_plate: e.target.value })}
                                placeholder="ex: AB-123-CD"
                            />
                        </div>
                        <button
                            className="primary-button"
                            style={{ width: '100%' }}
                            disabled={!bookingData.vehicle_brand || !bookingData.vehicle_model}
                            onClick={() => setStep(3)}
                        >
                            Continuer
                        </button>
                    </div>
                </div>
            )}

            {/* STEP 3: DATE */}
            {step === 3 && (
                <div className="booking-step-content animation-fadeIn">
                    <button className="back-btn" onClick={() => setStep(2)}>← Retour</button>
                    <h3>Sélectionnez une date</h3>
                    <div className="calendar-booking">
                        <div className="calendar-header-booking">
                            {availableDates[0]?.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' })}
                        </div>
                        <div className="calendar-dates-list">
                            {availableDates.map((date, i) => (
                                <button
                                    key={i}
                                    className={`date-select-card ${bookingData.date === date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' }) ? 'active' : ''}`}
                                    onClick={() => handleDateSelect(date)}
                                >
                                    <span className="date-day-name">{date.toLocaleDateString('fr-FR', { weekday: 'short' })}</span>
                                    <span className="date-day-number">{date.getDate()}</span>
                                    <span className="date-month-name">{date.toLocaleDateString('fr-FR', { month: 'short' })}</span>
                                </button>
                            ))}
                        </div>
                    </div>
                </div>
            )}

            {/* STEP 4: TIME */}
            {step === 4 && (
                <div className="booking-step-content animation-fadeIn">
                    <button className="back-btn" onClick={() => setStep(3)}>← Retour</button>
                    <h3>Horaires pour le {bookingData.date}</h3>
                    <div className="slots-grid">
                        {availableSlots.length > 0 ? availableSlots.map(slot => (
                            <button key={slot} className="time-slot-btn" onClick={() => handleTimeSelect(slot)}>
                                {slot}
                            </button>
                        )) : (
                            <p>Aucun créneau disponible pour cette date.</p>
                        )}
                    </div>
                </div>
            )}

            {/* STEP 5: CONTACT & CONFIRMATION */}
            {step === 5 && (
                <div className="booking-step-content animation-fadeIn">
                    <button className="back-btn" onClick={() => setStep(4)}>← Retour</button>
                    <h3>Confirmation & Coordonnées</h3>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2rem', textAlign: 'left', marginTop: '1rem' }}>
                        <div>
                            <p style={{ fontWeight: 'bold', color: '#facc15', marginBottom: '1rem' }}>Vos Informations</p>
                            <div className="form-group" style={{ marginBottom: '1rem' }}>
                                <label>Prénom & Nom</label>
                                <input className="admin-input" value={`${bookingData.firstName} ${bookingData.lastName}`} disabled />
                            </div>
                            <div className="form-group" style={{ marginBottom: '1rem' }}>
                                <label>Email</label>
                                <input className="admin-input" value={bookingData.email} disabled />
                            </div>
                            <div className="form-group">
                                <label>Téléphone</label>
                                <input
                                    className="admin-input"
                                    value={bookingData.phone}
                                    onChange={e => setBookingData({ ...bookingData, phone: e.target.value })}
                                    placeholder="ex: 06..."
                                />
                            </div>
                        </div>

                        <div className="summary-box" style={{ margin: 0 }}>
                            <p style={{ fontWeight: 'bold', color: '#facc15', marginBottom: '1rem' }}>Récapitulatif</p>
                            <p><strong>Service :</strong> {services.find(s => s.id === bookingData.service)?.label}</p>
                            <p><strong>Véhicule :</strong> {bookingData.vehicle_brand} {bookingData.vehicle_model} ({bookingData.vehicle_plate || 'N/C'})</p>
                            <p><strong>Date :</strong> {bookingData.date}</p>
                            <p><strong>Heure :</strong> {bookingData.time}</p>

                            <button
                                className="primary-button"
                                style={{ width: '100%', marginTop: '1.5rem' }}
                                onClick={handleConfirmBooking}
                                disabled={!bookingData.phone}
                            >
                                Valider le rendez-vous
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* STEP 6: SUCCESS */}
            {step === 6 && (
                <div className="booking-step-content animation-fadeIn">
                    <div className="success-confirmation">
                        <div className="success-icon">✓</div>
                        <h3>Rendez-vous enregistré !</h3>
                        <p>Votre demande pour le <strong>{bookingData.date}</strong> à <strong>{bookingData.time}</strong> a été transmise.</p>
                        <div className="summary-box">
                            <p><strong>Service :</strong> {services.find(s => s.id === bookingData.service)?.label}</p>
                            <p><strong>Véhicule :</strong> {bookingData.vehicle_brand} {bookingData.vehicle_model}</p>
                            <p><strong>Lieu :</strong> 24 Rue de l'Aiguille, 31000 Toulouse</p>
                        </div>
                        <button className="primary-button" onClick={resetBooking}>Prendre un autre RDV</button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default BookingSystem;
